import Joi from 'joi';
import { useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import {
    OnlyFansSubscriberMessageUsersAutoMessageDetails,
    OnlyFansSubscriberMessageUsersAutoMessageDetailsSchema,
} from '../types/messageUsersAutoSendWelcomeMessage';
import useAxios from './useAxios';

export interface MessageUsersOnlineWelcomeMessage {
    _id?: string;
    subscriberId: string;
    name: string;
    messageDetails: OnlyFansSubscriberMessageUsersAutoMessageDetails;
    sentCount: number;
    createdAt?: Date;
    updatedAt?: Date;
}

export const MessageUsersOnlineWelcomeMessageSchema = Joi.object<MessageUsersOnlineWelcomeMessage>({
    _id: Joi.string().optional(),
    subscriberId: Joi.string().required(),
    name: Joi.string().trim().required().default(''),
    messageDetails: OnlyFansSubscriberMessageUsersAutoMessageDetailsSchema.required(),
    sentCount: Joi.number().optional().default(0),
})
    .options({ stripUnknown: true })
    .required();

const useMessageUsersAutoWelcomeMessageOnlineUsers = (queryStr: string, active?: boolean) => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const params = useParams();
    const axios = useAxios();

    const [isSavingAutoWelcomeMessageOnlineUsers, setIsSavingAutoWelcomeMessageOnlineUsers] = useState<boolean>(false);
    const [isDeletingAutoWelcomeMessageOnlineUsers, setIsDeletingAutoWelcomeMessageOnlineUsers] = useState<boolean>(false);
    const [isUpdatingAutoWelcomeMessageOnlineUsers, setIsUpdatingAutoWelcomeMessageOnlineUsers] = useState<boolean>(false);

    /**
     * Fetch Welcome Message to Online Fans templates
     * @returns List of Welcome Message to Online Fans templates
     */
    const fetchTotalSent = async (): Promise<MessageUsersOnlineWelcomeMessage[]> => {
        if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId) {
            const query: string = `${settingsContext.routes.messageUsers.base}${params.userId}/autoWelcomeMessagesOnlineUsers/templates?${
                active !== undefined ? `active=${active}` : ''
            }${queryStr !== '' ? '&query=' + queryStr : ''}`;

            return axios
                .get(query)
                .then(async response => {
                    if (response.status === 200) {
                        return response.data;
                    }

                    return [];
                })
                .catch(error => {
                    console.error(error);

                    return [];
                });
        }

        return [];
    };

    const queryResult = useQuery(['messageUsersOnlineWelcomeMessage', params.userId, queryStr, active], () => fetchTotalSent(), {
        refetchOnWindowFocus: false,
        // staleTime: 5 * 60 * 1000, // 5 minutes
        enabled: userContext.jwtToken && settingsContext.apiKey && params.userId ? true : false,
    });

    /**
     * Create a new Welcome Message to Online Fans template
     * @param name Name
     * @param messageDetails Message Details
     * @returns New Welcome Message to Online Fans template
     */
    const saveMessageOnlineUsersWelcomeMessage = async (
        subscriberId: number,
        name: string,
        messageDetails: OnlyFansSubscriberMessageUsersAutoMessageDetails,
    ): Promise<MessageUsersOnlineWelcomeMessage | null> => {
        if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId) {
            const postData = {
                subscriberId,
                name,
                messageDetails,
            };

            const postUrl: string = `${settingsContext.routes.messageUsers.base}${params.userId}/autoWelcomeMessagesOnlineUsers/templates`;

            setIsSavingAutoWelcomeMessageOnlineUsers(true);

            return axios
                .post(postUrl, postData)
                .then(async response => {
                    setIsSavingAutoWelcomeMessageOnlineUsers(false);

                    if (response.status === 200) {
                        return response.data;
                    }

                    return null;
                })
                .catch(error => {
                    console.error(error);

                    setIsSavingAutoWelcomeMessageOnlineUsers(false);

                    return null;
                });
        }

        return null;
    };

    /**
     * Update a Welcome Message to Online Fans template
     * @param welcomeMessageId Welcome Message ID
     * @param updateData Update Data
     * @returns Updated Welcome Message to Online Fans template
     */
    const updateMessageOnlineUsersWelcomeMessage = async (
        welcomeMessageId: string,
        updateData: MessageUsersOnlineWelcomeMessage,
    ): Promise<MessageUsersOnlineWelcomeMessage | null> => {
        if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId) {
            const putUrl: string = `${settingsContext.routes.messageUsers.base}${params.userId}/autoWelcomeMessagesOnlineUsers/templates/${welcomeMessageId}`;

            setIsUpdatingAutoWelcomeMessageOnlineUsers(true);

            return axios
                .put(putUrl, updateData)
                .then(async response => {
                    setIsUpdatingAutoWelcomeMessageOnlineUsers(false);

                    if (response.status === 200) {
                        return response.data;
                    }

                    return null;
                })
                .catch(error => {
                    console.error(error);

                    setIsUpdatingAutoWelcomeMessageOnlineUsers(false);

                    return null;
                });
        }

        return null;
    };

    /**
     * Delete a Welcome Message to Online Fans template
     * @param welcomeMessageId Template ID
     * @returns Deletion result
     */
    const deleteMessageOnlineUsersWelcomeMessage = async (welcomeMessageId: string): Promise<{ success: boolean; _id: string }> => {
        if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId) {
            const deleteUrl: string = `${settingsContext.routes.messageUsers.base}${params.userId}/autoWelcomeMessagesOnlineUsers/templates/${welcomeMessageId}`;

            setIsDeletingAutoWelcomeMessageOnlineUsers(true);

            return axios
                .delete(deleteUrl)
                .then(async response => {
                    setIsDeletingAutoWelcomeMessageOnlineUsers(false);

                    if (response.status === 200) {
                        return response.data;
                    }

                    return { success: false, _id: welcomeMessageId };
                })
                .catch(error => {
                    console.error(error);

                    setIsDeletingAutoWelcomeMessageOnlineUsers(false);

                    return { success: false, _id: welcomeMessageId };
                });
        }

        return { success: false, _id: welcomeMessageId };
    };

    /**
     * Set the active status of a Welcome Message template
     * @param welcomeMessageId Template ID
     * @param active Active status to set
     * @returns Updated Welcome Message template
     */
    const setMessageOnlineUsersWelcomeMessageActive = async (
        welcomeMessageId: string,
        active: boolean,
    ): Promise<MessageUsersOnlineWelcomeMessage | null> => {
        if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId) {
            const putUrl: string = `${settingsContext.routes.messageUsers.base}${params.userId}/autoWelcomeMessagesOnlineUsers/templates/${welcomeMessageId}/active`;

            setIsUpdatingAutoWelcomeMessageOnlineUsers(true);

            return axios
                .put(putUrl, { active })
                .then(async response => {
                    setIsUpdatingAutoWelcomeMessageOnlineUsers(false);

                    if (response.status === 200) {
                        return response.data;
                    }

                    return null;
                })
                .catch(error => {
                    console.error(error);

                    setIsUpdatingAutoWelcomeMessageOnlineUsers(false);

                    return null;
                });
        }

        return null;
    };

    return {
        ...queryResult,
        saveMessageOnlineUsersWelcomeMessage,
        isSavingAutoWelcomeMessageOnlineUsers,
        updateMessageOnlineUsersWelcomeMessage,
        isUpdatingAutoWelcomeMessageOnlineUsers,
        deleteMessageOnlineUsersWelcomeMessage,
        isDeletingAutoWelcomeMessageOnlineUsers,
        setMessageOnlineUsersWelcomeMessageActive,
    };
};

export default useMessageUsersAutoWelcomeMessageOnlineUsers;
